import * as React from 'react';
import { wrapDisplayName } from 'recompose';
import { ActionCreator } from 'redux';
import { connect, useDispatch } from 'react-redux';

export interface TrackProps<P = {}> {
  activate: (props: P) => void;
  deactivate: () => void;
}

export type LifecycleActions<P> = {
  activate: ActionCreator<{ type: string; payload?: P }>;
  deactivate?: ActionCreator<{ type: string }>;
};

export function track<P = {}>(mapDispatchToProps: LifecycleActions<P>) {
  return (BaseComponent: React.ComponentClass<P> | React.FC<P>) => {
    class ActiveComponent extends React.Component<P & TrackProps<P>> {
      constructor(props: P & TrackProps<P>) {
        super(props);
        const { activate } = props;
        activate && activate(props);
      }

      componentWillUnmount() {
        const { deactivate } = this.props;
        deactivate && deactivate();
      }

      render() {
        return <BaseComponent {...this.props} />;
      }
    }

    const ConnectedComponent = connect(
      undefined,
      mapDispatchToProps
      // @ts-ignore
    )(ActiveComponent);

    if (process.env.NODE_ENV !== 'production') {
      ConnectedComponent.displayName = wrapDisplayName(BaseComponent, 'tracked');
    }

    return ConnectedComponent;
  };
}

export function useTrack<P = {}>({ activate, deactivate }: LifecycleActions<P>, props: P) {
  const dispatch = useDispatch();

  if (__ISSERVER__) {
    return;
  }

  React.useEffect(() => {
    activate && dispatch(activate(props));
    return () => {
      deactivate && dispatch(deactivate());
    };
  }, []);
}
